/**
 * Safe regex validation utilities with ReDoS prevention.
 *
 * Backend connector schemas include regex patterns for field validation.
 * These patterns come from the server and could potentially be malicious
 * or computationally expensive. This module provides:
 *
 *   1. Static dangerous-pattern detection (nested quantifiers, etc.)
 *   2. A synchronous test with timing warnings (safeRegexTest / safeRegexTestSync)
 *   3. An async test using a Web Worker with hard timeout via worker.terminate()
 *      (safeRegexTestAsync)
 *
 * PHILOSOPHY: Fail open. On any error, timeout, or dangerous pattern, return
 * true so that server-side validation is the authority.
 */

import { isDangerousPattern } from './dangerousPatterns';

const MAX_PATTERN_LENGTH = 200;
const REGEX_TIMEOUT_MS = 100;
const WORKER_TIMEOUT_MS = 200;

// ---------------------------------------------------------------------------
// Pattern validation
// ---------------------------------------------------------------------------

/**
 * Check if a regex pattern is syntactically valid and within length limits.
 */
export function isValidPattern(pattern: string): boolean {
  if (!pattern || pattern.length > MAX_PATTERN_LENGTH) {
    return false;
  }
  try {
    new RegExp(pattern); // nosemgrep: rules.eslint.detect-non-literal-regexp  -- ReDoS-guard util validating pattern syntax/length before use
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Synchronous API (used by Zod refine and other sync callers)
// ---------------------------------------------------------------------------

/**
 * Synchronous regex test with dangerous-pattern pre-check and timing warning.
 *
 * This is the default export for callers that need a synchronous return value
 * (e.g. Zod `.refine()` callbacks). It cannot truly terminate a hung regex --
 * use safeRegexTestAsync for that.
 */
export function safeRegexTest(pattern: string, value: string): boolean {
  return safeRegexTestSync(pattern, value);
}

export function safeRegexTestSync(pattern: string, value: string): boolean {
  if (!isValidPattern(pattern)) {
    console.warn(`[safeRegex] Invalid or too-long pattern, skipping: ${pattern.slice(0, 50)}`);
    return true; // Fail open
  }

  if (isDangerousPattern(pattern)) {
    console.warn(`[safeRegex] Dangerous pattern detected, skipping: ${pattern.slice(0, 50)}`);
    return true; // Fail open
  }

  try {
    const regex = new RegExp(pattern); // nosemgrep: rules.eslint.detect-non-literal-regexp  -- ReDoS-guard util; pattern already length- and dangerous-pattern-checked above
    const start = performance.now();
    const result = regex.test(value);
    const elapsed = performance.now() - start;

    if (elapsed > REGEX_TIMEOUT_MS) {
      console.warn(
        `[safeRegex] Regex validation took ${elapsed.toFixed(1)}ms (>${REGEX_TIMEOUT_MS}ms), ` +
          `pattern: ${pattern.slice(0, 50)}...`,
      );
      return true; // Fail open -- let server validate
    }

    return result;
  } catch (error) {
    console.warn(`[safeRegex] Regex error, failing open:`, error);
    return true; // Fail open
  }
}

// ---------------------------------------------------------------------------
// Web Worker support detection
// ---------------------------------------------------------------------------

/**
 * Returns true if the current environment supports Web Workers with Blob URLs.
 * We cache the result after the first call.
 */
let _workersSupported: boolean | null = null;

export function supportsWorkers(): boolean {
  if (_workersSupported !== null) {
    return _workersSupported;
  }
  try {
    _workersSupported =
      typeof Worker !== 'undefined' &&
      typeof Blob !== 'undefined' &&
      typeof URL !== 'undefined' &&
      typeof URL.createObjectURL === 'function';
  } catch {
    _workersSupported = false;
  }
  return _workersSupported;
}

/**
 * Reset the cached worker-support flag. Useful for testing.
 * @internal
 */
export function _resetWorkersSupported(): void {
  _workersSupported = null;
}

// ---------------------------------------------------------------------------
// Async API with Web Worker isolation
// ---------------------------------------------------------------------------

/**
 * Inline worker source. We embed the worker code as a string so that we can
 * create a Blob URL without needing a separate bundled asset. Vite strips
 * type annotations at build time, but this string is plain JS.
 */
const WORKER_SOURCE = `
self.onmessage = function(event) {
  var data = event.data;
  try {
    var regex = new RegExp(data.pattern);
    var result = regex.test(data.value);
    self.postMessage({ result: result });
  } catch (err) {
    self.postMessage({ error: err instanceof Error ? err.message : String(err) });
  }
};
`;

/**
 * Async regex test that runs the regex in a Web Worker.
 *
 * If the regex takes longer than WORKER_TIMEOUT_MS, the worker is terminated
 * and this function returns true (fail open). This is the strongest ReDoS
 * protection because worker.terminate() actually kills the thread.
 *
 * Falls back to the synchronous implementation when Workers are unavailable.
 */
export async function safeRegexTestAsync(pattern: string, value: string): Promise<boolean> {
  // Quick checks that don't need a worker
  if (!isValidPattern(pattern)) {
    console.warn(`[safeRegex] Invalid or too-long pattern, skipping: ${pattern.slice(0, 50)}`);
    return true;
  }

  if (isDangerousPattern(pattern)) {
    console.warn(`[safeRegex] Dangerous pattern detected, skipping: ${pattern.slice(0, 50)}`);
    return true;
  }

  // Fall back to sync if Workers are not available
  if (!supportsWorkers()) {
    return safeRegexTestSync(pattern, value);
  }

  let worker: Worker | null = null;
  let blobUrl: string | null = null;

  try {
    const blob = new Blob([WORKER_SOURCE], { type: 'application/javascript' });
    blobUrl = URL.createObjectURL(blob);
    worker = new Worker(blobUrl);

    const result = await new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        console.warn(
          `[safeRegex] Worker timed out after ${WORKER_TIMEOUT_MS}ms, ` +
            `pattern: ${pattern.slice(0, 50)}...`,
        );
        worker?.terminate();
        resolve(true); // Fail open
      }, WORKER_TIMEOUT_MS);

      worker!.onmessage = (event: MessageEvent<{ result?: boolean; error?: string }>) => {
        clearTimeout(timer);
        if (event.data.error) {
          console.warn(`[safeRegex] Worker regex error:`, event.data.error);
          resolve(true); // Fail open
        } else {
          resolve(event.data.result ?? true);
        }
      };

      worker!.onerror = () => {
        clearTimeout(timer);
        console.warn(`[safeRegex] Worker error, failing open`);
        resolve(true); // Fail open
      };

      worker!.postMessage({ pattern, value });
    });

    return result;
  } catch (error) {
    console.warn(`[safeRegex] Failed to create worker, falling back to sync:`, error);
    return safeRegexTestSync(pattern, value);
  } finally {
    worker?.terminate();
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }
  }
}
